// The music a tenant can pick for a video creative.
//
// Shared because two things must agree on it exactly: the picker in the
// manager, and the renderer in the API that embeds the file. A track in
// one and not the other is a post that previews with music and ships
// silent.
//
// WHERE THE FILES COME FROM
// -------------------------
// Meta Sound Collection: https://www.facebook.com/sound/collection
//
// It has no API — you audition tracks in the browser, click Download, and
// get an MP3. Upload those to Spaces under `audio/` and add a row here.
// That manual step is deliberate: picking what a brokerage should sound
// like is a curation decision, not something to generate.
//
// LICENCE
// -------
// Sound Collection is royalty-free and Meta states it "can be used for
// commercial purposes like ads", which covers both organic posts and paid
// campaigns. Do NOT add tracks from Instagram's *licensed music library* —
// that is personal, non-commercial use only and business accounts are
// locked out of it regardless.
//
// Anything from another source needs its licence checked against the fact
// that this is a multi-tenant product: many "free" libraries permit using
// a track in your own video but prohibit offering it to end users to
// choose from, which is what this list does.

export interface MusicTrack {
  /** Stable id. Stored on the post, so never renumber these. */
  id: string;
  /** What the picker shows. */
  name: string;
  /** Artist, for the attribution record — not shown in the creative. */
  artist: string;
  /** Public URL of the MP3, normally in Spaces under `audio/`. */
  url: string;
  /** One-word feel, used to group the picker. */
  mood: 'calm' | 'uplifting' | 'cinematic' | 'modern';
  /** Track length in seconds. Clips are shorter; this is for the label. */
  seconds: number;
  /**
   * Where Sound Collection listed it, kept so a licence question years
   * from now can be answered without guessing.
   */
  source: 'meta-sound-collection';
}

/**
 * The catalogue. Empty until someone curates it — see the note above.
 *
 * Deliberately not seeded with placeholder URLs: a picker offering tracks
 * that 404 at render time is worse than a picker that is honestly empty,
 * because the failure surfaces in a tenant's published post rather than
 * here.
 */
export const MUSIC_TRACKS: MusicTrack[] = [];

/** Look a track up by id. Returns null for an unknown or removed one. */
export function musicTrackById(id: string | null | undefined): MusicTrack | null {
  if (!id) return null;
  return MUSIC_TRACKS.find((t) => t.id === id) ?? null;
}
